import React, { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

import { ColumnHeader } from 'directory/listings/notification-modals/components/ColumnHeader';
import {
    LocationCell,
    NestedDataCell,
    SingleDataCell,
} from 'directory/listings/notification-modals/components/TableCells';
import {
    ICON_PATHS,
    TRANSLATION_NAMESPACE,
    ROW_STATUS,
    FIELD_TYPE,
} from 'directory/listings/notification-modals/constants';
import { LocationDataRow } from 'directory/listings/notification-modals/types';
import 'directory/listings/notification-modals/components/DataTable/DataTable.scss';

export const getDefaultColumns = (
    tableData?: LocationDataRow[],
    isCompletionView = false,
): Column<LocationDataRow>[] => {
    const firstRow = tableData?.[0];
    const dynamicFieldIcon = firstRow?.fieldIcon || 'icon-address-card';
    const dynamicFieldLabelKey = firstRow?.field || 'UNKNOWN';

    return [
        {
            Header: () => {
                const { t } = useTranslation(TRANSLATION_NAMESPACE);

                return (
                    <ColumnHeader
                        icon={
                            <img
                                src={ICON_PATHS.LOCATION}
                                alt="location icon"
                                className="column-header__location-icon"
                            />
                        }
                        title={t('LOCATIONS')}
                        subtitle={t('ONLY_VALID_CREDENTIALS')}
                    />
                );
            },
            accessor: 'locationName',
            Cell: ({ row: { original } }: any): ReactNode => <LocationCell locationName={original.locationName} />,
        },
        {
            Header: () => {
                const { t } = useTranslation(TRANSLATION_NAMESPACE);

                return (
                    <ColumnHeader
                        icon={<img src={ICON_PATHS.GOOGLE} alt="google logo" className="column-header__google-icon" />}
                        title={t('GOOGLE')}
                        subtitle={t(dynamicFieldLabelKey)}
                        subtitleIcon={dynamicFieldIcon}
                    />
                );
            },
            accessor: 'googleData',
            Cell: ({ row: { original } }: any): ReactNode => {
                const { fieldIcon, field } = original;

                if (original.fieldType === FIELD_TYPE.NESTED && Array.isArray(original.googleData)) {
                    return <NestedDataCell data={original.googleData} icon={fieldIcon} labelKey={field} />;
                }

                return <SingleDataCell data={original.googleData} icon={fieldIcon} />;
            },
        },
        {
            Header: () => {
                const { t } = useTranslation(TRANSLATION_NAMESPACE);

                return (
                    <ColumnHeader
                        icon={
                            <img
                                src={ICON_PATHS.REPUTATION}
                                alt="reputation logo"
                                className="column-header__reputation-icon"
                            />
                        }
                        title={t('REPUTATION_PROFILE')}
                        subtitle={t(dynamicFieldLabelKey)}
                        subtitleIcon={dynamicFieldIcon}
                    />
                );
            },
            accessor: 'reputationData',
            Cell: ({ row }: any): ReactNode => {
                const { original } = row;
                const { status, reputationData, googleData, errorMessage, fieldIcon, field } = original;
                const isMissing = status === ROW_STATUS.MISSING;

                if (isCompletionView) {
                    if (status === ROW_STATUS.SUCCESS) {
                        if (original.fieldType === FIELD_TYPE.NESTED && Array.isArray(reputationData)) {
                            return (
                                <div className="d-flex align-items-start gap-2">
                                    <i className="icon-check-circle mr-2 data-table__status-icon--success" />
                                    <NestedDataCell data={reputationData} labelKey={field} />
                                </div>
                            );
                        }

                        return (
                            <div className="single-data-cell">
                                <i className="icon-check-circle mr-2 data-table__status-icon--success" />
                                <span>{reputationData || googleData || '—'}</span>
                            </div>
                        );
                    }

                    if (status === ROW_STATUS.ERROR || status === ROW_STATUS.MISSING) {
                        if (original.fieldType === FIELD_TYPE.NESTED && Array.isArray(reputationData)) {
                            return (
                                <div>
                                    <div className="d-flex align-items-start gap-2">
                                        <i className="icon-check-circle mr-2 data-table__status-icon--error" />
                                        <NestedDataCell data={reputationData} labelKey={field} />
                                    </div>
                                    {errorMessage && (
                                        <small className="text-muted d-flex gap-2 mt-1">
                                            <i className="icon-exclamation-circle mr-2 data-table__status-icon--muted" />
                                            {errorMessage}
                                        </small>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <>
                                <div className="single-data-cell">
                                    <i className="icon-times-circle mr-2 data-table__status-icon--error" />
                                    <span>{reputationData || googleData || '—'}</span>
                                </div>
                                {errorMessage && (reputationData || googleData) && (
                                    <small className="text-muted d-flex gap-2 mt-1">
                                        <i className="icon-exclamation-circle mr-2 data-table__status-icon--muted" />
                                        {errorMessage}
                                    </small>
                                )}
                            </>
                        );
                    }

                    return <span className="text-muted">—</span>;
                }

                if (original.fieldType === FIELD_TYPE.NESTED && Array.isArray(reputationData)) {
                    return <NestedDataCell data={reputationData} icon={fieldIcon} labelKey={field} />;
                }

                return <SingleDataCell data={reputationData} isMissing={isMissing} icon={fieldIcon} />;
            },
        },
    ];
};
